import { PxtraApiClientLegacy } from '@pxtra-client-lib-legacy';
import {
  ReceiptBenefitEnum,
  CancelablePromise,
  PatchedPostReceiptRequest,
  PostReceiptRequest,
  Receipt,
  ReceiptStatusEnum
} from '@pxtra-client-lib-legacy/gen';
import { format } from 'date-fns';
import { makeAutoObservable, runInAction, when } from 'mobx';
import Util from '../../misc/util';
import AuthenticationStore from '../authenticationStore';
import CompanyStore from '../companyStore';

class ReceiptStore {
  private apiClient: PxtraApiClientLegacy;
  private dateFormat = 'yyyy-MM-dd';

  private _receiptList: Map<string, Receipt>;
  private _currentId?: string;
  private _queryStatus?: ReceiptStatusEnum;

  public constructor(
    apiClient: PxtraApiClientLegacy,
    private companyStore: CompanyStore,
    private authenticationStore: AuthenticationStore
  ) {
    this.apiClient = apiClient;
    this._receiptList = new Map<string, Receipt>();
    makeAutoObservable(this);
    when(
      () => !this.authenticationStore.isLoggedIn,
      () => {
        this._clear();
      }
    );
  }

  private _clear() {
    this._receiptList = new Map<string, Receipt>();
    this._currentId = undefined;
    this._queryStatus = undefined;
  }

  public get receiptList() {
    return this._receiptList ? Array.from(this._receiptList.values()) : new Array<Receipt>();
  }

  public get current() {
    if (this._currentId && this._receiptList) {
      return this._receiptList.get(this._currentId);
    }
  }

  public get queryStatus() {
    return this._queryStatus;
  }

  public set queryStatus(status: ReceiptStatusEnum | undefined) {
    this._queryStatus = status;
  }

  public async getInfoByMonth(date: Date, employeeId?: string, benefits?: ReceiptBenefitEnum[]) {
    const dateAfter = Util.getFirstDayOfMonth(date);
    const dateBefore = Util.getLastDayOfMonth(date);
    return this.getInfo(employeeId, benefits, dateAfter, dateBefore);
  }

  public async getInfo(
    employeeId?: string,
    benefits?: ReceiptBenefitEnum[],
    dateAfter?: Date,
    dateBefore?: Date
  ) {
    if (!this.companyStore.company) throw Error('No company selected');

    return this.apiClient.receipts.receiptsInfoList({
      employee: employeeId,
      benefit: benefits,
      dateAfter: dateAfter ? format(dateAfter, this.dateFormat) : undefined,
      dateBefore: dateBefore ? format(dateBefore, this.dateFormat) : undefined,
      company: this.companyStore.company.id
    });
  }

  public async fetchList(dateAfter?: Date, dateBefore?: Date, employeeId?: string) {
    if (!this.companyStore.company) throw Error('No company selected');
    this._receiptList.clear();
    const response = await this.apiClient.receipts.receiptsList({
      dateAfter: dateAfter ? format(dateAfter, this.dateFormat) : undefined,
      dateBefore: dateBefore ? format(dateBefore, this.dateFormat) : undefined,
      employee: employeeId,
      company: this.companyStore.company.id
    });
    response.results && this.updateState(response.results);
  }

  public async fetchListByMonth(date: Date, employeeId?: string) {
    return this.fetchList(Util.getFirstDayOfMonth(date), Util.getLastDayOfMonth(date), employeeId);
  }

  public async fetchOne(id: string) {
    return this.handleResponse(() => {
      return this.apiClient.receipts.receiptsRetrieve({ id: id });
    });
  }

  public async create(data: PostReceiptRequest) {
    return this.handleResponse(() => {
      return this.apiClient.receipts.receiptsCreate({
        requestBody: { ...data, employee: data.employee }
      });
    });
  }

  public async update(id: string, data: PatchedPostReceiptRequest) {
    return this.handleResponse(() => {
      return this.apiClient.receipts.receiptsPartialUpdate({
        id: id,
        requestBody: data
      });
    });
  }

  public async updateCurrent(data: PatchedPostReceiptRequest) {
    if (this._currentId) {
      return this.update(this._currentId, data);
    }
    throw new Error('current receipt is not defined');
  }

  public async approve(id: string) {
    return this.handleResponse(() => {
      return this.apiClient.receipts.receiptsApproveUpdate({ id: id });
    });
  }

  public async approveCurrent() {
    if (this._currentId) {
      return this.approve(this._currentId);
    }
  }

  public async approveList(idList: string[]) {
    await Promise.all(idList.map((id) => this.approve(id)));
  }

  public async reject(id: string, note?: string) {
    return this.handleResponse(() => {
      return this.apiClient.receipts.receiptsRejectUpdate({
        id: id,
        requestBody: { note: note ?? '' }
      });
    });
  }

  public async rejectCurrent(note?: string) {
    if (this._currentId) {
      return this.reject(this._currentId, note);
    }
  }

  public async rejectList(idList: string[], note?: string) {
    await Promise.all(idList.map((id) => this.reject(id, note)));
  }

  public async settle(id: string) {
    return this.handleResponse(() => {
      return this.apiClient.receipts.receiptsSettleUpdate({ id: id });
    });
  }

  public async settleCurrent() {
    if (this._currentId) {
      return this.settle(this._currentId);
    }
  }

  public async settleList(idList: string[]) {
    await Promise.all(idList.map((id) => this.settle(id)));
  }

  public async delete(id: string) {
    await this.apiClient.receipts.receiptsDestroy({ id: id });
    runInAction(() => this._receiptList?.delete(id));
  }

  public async deleteCurrent() {
    if (this._currentId) {
      return this.delete(this._currentId);
    }
  }

  public async deleteList(idList: string[]) {
    await Promise.all(idList.map((id) => this.delete(id)));
  }

  public clearCurrent() {
    runInAction(() => (this._currentId = undefined));
  }

  private async handleResponse(request: () => CancelablePromise<Receipt>) {
    return request().then((response) => {
      this.updateState(response as Receipt);
    });
  }

  private updateState(reciept: Receipt | Receipt[]) {
    if (Array.isArray(reciept)) {
      runInAction(() => (this._receiptList = new Map(reciept.map((r) => [r.id, r]))));
    } else {
      runInAction(() => {
        this._currentId = reciept.id;
        this._receiptList.set(reciept.id, reciept);
      });
    }
  }
}

export default ReceiptStore;
